import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { configApi } from '../api/config';
import { listRecoveryQueue } from '../api/exchanges';
import { flowsApi } from '../api/flows';
import { mappingsApi } from '../api/mappings';
import { schemasApi } from '../api/schemas';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { DisabledActionButton } from '../components/DisabledActionButton';
import { PageHeader, TrustBoundaryNotice } from '../components/FeatureStatus';
import { FlowBuilderStep as ExtractedFlowBuilderStep, FlowBuilderSummary, type FlowBuilderConfig } from '../components/FlowBuilderSteps';
import { demoFeaturesEnabled } from '../runtime-features';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { PayloadViewer } from '../components/PayloadViewer';
import { KpiTrend } from '../components/Sparkline';
import { GuidedPayloadEditor } from '../components/GuidedPayloadEditor';
import { PollingIndicator } from '../components/PollingIndicator';
import { useAsyncData } from '../hooks/useAsyncData';
import { usePermission } from '../hooks/usePermission';
import { bucketCountsByTime, ONE_DAY_MS } from '../lib/timeBuckets';
import { inferFields, parseJson } from '../lib/fieldInference';
import { ConnectorDefinitionsPage, ConnectorInstancesPage } from './ConfigPages';
import { ExchangesPage } from './ExchangesPage';
import { MARITIME_DOCUMENT_TYPE_OPTIONS } from '../types/api';
import type {
  ConnectorProtocolFamily,
  DocumentType,
  EaiSecurityScheme,
  FlowRecord,
  FunctionalTestResult,
  MappingRecord,
  MappingRuleDraft,
  MappingRuleType,
  MappingVersionRecord,
  PartnerRecord,
  SchemaFieldNode,
} from '../types/api';

const EAI_DASHBOARD_POLL_INTERVAL_MS = 30_000;
const FLOW_BUILDER_DRAFT_KEY = 'oss-integration.flow-builder-draft.v1';

type PartnerStatus = 'ONBOARDING' | 'OPERATIONAL' | 'PAUSED' | 'OFFBOARDED';
type Environment = 'DEV' | 'TEST' | 'UAT' | 'PROD';
type TargetSystem = 'PMIS' | 'PCS' | 'TOS' | 'OSS internal app' | 'webhook' | 'Kafka topic' | 'SFTP export';

interface OnboardingDraft {
  name: string;
  code: string;
  legalName: string;
  environment: Environment;
  status: PartnerStatus;
  channel: ConnectorProtocolFamily;
  endpoint: string;
  messageTypes: DocumentType[];
  security: EaiSecurityScheme[];
  requestsPerMinute: number;
  payloadMaxMb: number;
  retryPolicy: string;
  retentionPolicy: string;
}

const messageTypes: DocumentType[] = [...MARITIME_DOCUMENT_TYPE_OPTIONS];
const protocols: ConnectorProtocolFamily[] = ['REST', 'WEBHOOK', 'SFTP', 'KAFKA', 'SOAP', 'MANUAL'];
const securitySchemes: EaiSecurityScheme[] = [
  'API_KEY',
  'OAUTH2_CLIENT',
  'HMAC_SIGNATURE',
  'MTLS_CERTIFICATE',
  'IP_ALLOWLIST',
  'SFTP_CREDENTIALS',
  'KAFKA_TOPIC_ACL',
];
const mappingTypes: MappingRuleType[] = ['direct', 'constant', 'dateFormat', 'enumTranslation', 'concat', 'conditional', 'array'];
const targets: TargetSystem[] = ['PMIS', 'PCS', 'TOS', 'OSS internal app', 'webhook', 'Kafka topic', 'SFTP export'];

const cuscarSample = JSON.stringify({
  manifestNo: 'CUS-2026-00042',
  vessel: { name: 'NWM Atlas', imo: '9321483', voyage: 'NW126' },
  port: { load: 'MACAS', discharge: 'MAPTM' },
  arrivalDate: '2026-05-21',
  containers: [
    { number: 'MSCU1234567', isoCode: '22G1', weightKg: 14800, seal: 'SEAL-01' },
    { number: 'MSCU7654321', isoCode: '45G1', weightKg: 20120, seal: 'SEAL-02' },
  ],
  declarant: { code: 'PARTNER_A', name: 'Atlantic Customs Broker' },
}, null, 2);

const canonicalCargoManifest = {
  cargoManifest: {
    reference: 'CUS-2026-00042',
    messageType: 'CUSCAR',
    receivedFrom: 'PARTNER_A',
  },
  vessel: {
    imo: '9321483',
    name: 'NWM Atlas',
    voyage: 'NW126',
  },
  ports: {
    load: 'MACAS',
    discharge: 'MAPTM',
  },
  containers: [
    { number: 'MSCU1234567', equipmentType: '22G1', grossWeightKg: 14800, sealNumber: 'SEAL-01' },
  ],
};

const defaultMappingRules: MappingRuleDraft[] = [
  { id: 'rule-imo', type: 'direct', sourcePath: 'source.vessel.imo', targetPath: 'canonical.vessel.imo', expression: 'source.vessel.imo' },
  { id: 'rule-reference', type: 'direct', sourcePath: 'source.manifestNo', targetPath: 'canonical.cargoManifest.reference', expression: 'source.manifestNo' },
  { id: 'rule-container', type: 'array', sourcePath: 'source.containers[].number', targetPath: 'canonical.containers[].number', expression: 'source.containers.map(container => container.number)' },
  { id: 'rule-type', type: 'constant', sourcePath: '', targetPath: 'canonical.cargoManifest.messageType', expression: '"CUSCAR"' },
];

const onboardingSteps = ['Partner identity', 'Integration channel', 'Message types', 'Security', 'Quotas and limits', 'Review and create'];
const flowSteps = ['Source', 'Message', 'Mapping', 'Target', 'Runtime policy', 'Publish'];

function initialOnboardingDraft(): OnboardingDraft {
  return {
    name: '',
    code: '',
    legalName: '',
    environment: 'DEV',
    status: 'ONBOARDING',
    channel: 'REST',
    endpoint: '',
    messageTypes: [],
    security: [],
    requestsPerMinute: 60,
    payloadMaxMb: 10,
    retryPolicy: '3 attempts with exponential backoff',
    retentionPolicy: '30 days',
  };
}

export function EaiDashboardPage() {
  const { search } = useLocation();
  const [pollingPaused, setPollingPaused] = useState(false);
  const pollIntervalMs = pollingPaused ? undefined : EAI_DASHBOARD_POLL_INTERVAL_MS;
  const partnersRequest = useAsyncData(() => configApi.partners.list({ page: 1, pageSize: 100 }), [], { pollIntervalMs });
  const flowsRequest = useAsyncData(() => flowsApi.list({ page: 1, pageSize: 50 }), [], { pollIntervalMs });
  const executionsRequest = useAsyncData(() => flowsApi.executions({ page: 1, pageSize: 50 }), [], { pollIntervalMs });
  // Same query the recovery queue page (/dlq, ExchangesPage in recoveryMode) uses -- see
  // listRecoveryQueue's doc comment. Sharing it means this card can never show a number that
  // doesn't match what "Review recovery queue" actually opens.
  const recoveryRequest = useAsyncData(() => listRecoveryQueue(), [], { pollIntervalMs });
  const { data: partnerData } = partnersRequest;
  const { data: flowData } = flowsRequest;
  const { data: executionData } = executionsRequest;
  const { data: recoveryData } = recoveryRequest;

  const partners = partnerData?.data ?? [];
  const flows = flowData?.data ?? [];
  const executions = executionData?.data ?? [];
  const recoveryQueue = recoveryData?.data ?? [];
  // Lists are fetched one page at a time; headline counts must use the server-side total,
  // otherwise they silently cap at the page size (e.g. 50 of 179 failed exchanges).
  const recoveryTotal = recoveryData?.total ?? recoveryQueue.length;
  const recoveryTruncated = recoveryTotal > recoveryQueue.length;
  const flowsTotal = flowData?.total ?? flows.length;
  const activeFlows = flows.filter((flow) => flow.selectedVersion.lifecycleState === 'ACTIVE').length;
  const failedSources = [
    partnersRequest.error ? 'partners' : null,
    flowsRequest.error ? 'integrations' : null,
    executionsRequest.error ? 'executions' : null,
    recoveryRequest.error ? 'recovery queue' : null,
  ].filter((source): source is string => source !== null);
  const retryFailedSources = () => {
    for (const request of [partnersRequest, flowsRequest, executionsRequest, recoveryRequest]) {
      if (request.error) void request.refresh();
    }
  };
  const kpiValue = (request: { data: unknown; isLoading: boolean; error: string | null }, value: number) =>
    request.error && !request.data ? 'Unavailable' : request.isLoading && !request.data ? '...' : value;
  const partnersById = new Map(partners.map((partner) => [partner.id, partner]));
  const flowsById = new Map(flows.map((flow) => [flow.id, flow]));
  // Trend computed from the same recovery-queue rows shown by the "Review recovery queue" link,
  // bucketed by day. See src/lib/timeBuckets.ts for the bucketing/trend definition.
  const recoveryTrend = bucketCountsByTime(
    recoveryQueue.map((exchange) => exchange.createdAt),
    7,
    ONE_DAY_MS,
  );

  return (
    <div className="page-stack">
      <section className="hero eai-hero">
        <div>
          <span>OSS INTEGRATION</span>
          <h1>Set up and run integrations</h1>
          <p>Start with a partner and message type, choose how messages are delivered, test the payload, publish the flow, then monitor traffic and recover failures.</p>
        </div>
        <div className="panel-actions">
          <PollingIndicator paused={pollingPaused} onToggle={() => setPollingPaused((paused) => !paused)} />
          <Link className="button-link primary-link" to={{ pathname: '/flows/builder', search }}>Set up integration</Link>
          <Link className="button-link" to={{ pathname: '/executions', search }}>Monitor traffic</Link>
          <Link className="button-link" to={{ pathname: '/dlq', search }}>Recover failures</Link>
          {demoFeaturesEnabled() ? <Link className="button-link" to={{ pathname: '/demo/cuscar', search }}>Open CUSCAR demo</Link> : null}
        </div>
      </section>

      <section className="eai-journey" aria-label="Integration lifecycle">
        {['Partner', 'Message', 'Delivery', 'Rules', 'Test', 'Publish', 'Monitor', 'Recover'].map((item, index) => (
          <article className="journey-step" key={item}>
            <span>{index + 1}</span>
            <strong>{item}</strong>
          </article>
        ))}
      </section>

      {failedSources.length ? (
        <ErrorState
          message={`Some dashboard data could not be loaded (${failedSources.join(', ')}). The figures below may be incomplete.`}
          onRetry={retryFailedSources}
        />
      ) : null}

      <section className="recovery-callout" aria-labelledby="recovery-heading">
        <div>
          <span className="operations-card-label">Recovery candidates</span>
          <strong id="recovery-heading">{kpiValue(recoveryRequest, recoveryTotal)}</strong>
          <p>
            Failed, waiting-retry, or dead-lettered exchanges across the whole system -- exactly what "Review recovery
            queue" opens below. This is a review queue, not a guaranteed unresolved-incident count; replayed outcomes
            remain historical until their status changes.
          </p>
          <KpiTrend buckets={recoveryTrend} goodDirection="down" periodLabel="prior day" />
          {recoveryTruncated ? (
            <p className="field-hint">Trend based on the {recoveryQueue.length} most recent of {recoveryTotal} candidates.</p>
          ) : null}
        </div>
        <Link className="button-link recovery-action" to={{ pathname: '/dlq', search }}>Review recovery queue</Link>
      </section>

      <section className="operations-card-grid dashboard-summary-grid">
        <Kpi label="Partners" value={kpiValue(partnersRequest, partnerData?.total ?? partners.length)} detail="Configured trading partners and onboarding drafts." />
        <Kpi
          label="Active integrations"
          value={kpiValue(flowsRequest, activeFlows)}
          detail={
            flowsTotal > flows.length
              ? `Among the ${flows.length} most recent of ${flowsTotal} flows across draft, approved, and active states.`
              : `${flowsTotal} total flows across draft, approved, and active states.`
          }
        />
      </section>

      <section className="page-grid two-columns">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Recommended next step</h2>
              <p>The console keeps advanced configuration available, but daily work starts here.</p>
            </div>
          </div>
          <div className="stack-list">
            <GuidedAction title="Set up an integration" detail="Guided setup for partner, message, delivery channel, mapping, runtime policy, and publish." to="/flows/builder" search={search} />
            <GuidedAction title="Add a delivery channel" detail="Create the concrete endpoint an active integration uses to send messages automatically." to="/connectors" search={search} />
            <GuidedAction title="Monitor active traffic" detail="Review executions, failed messages, retries, and replay requests." to="/executions" search={search} />
          </div>
        </section>
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Recent execution posture</h2>
              <p>Clear signal for monitoring failures after publication.</p>
            </div>
          </div>
          {executions.length ? (
            <div className="table-scroll" tabIndex={0} aria-label="Recent execution details">
            <table className="data-table compact-table mapping-version-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Partner</th>
                  <th>Flow</th>
                  <th>Status</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {executions.slice(0, 5).map((execution) => (
                  <tr key={execution.id}>
                    <td title={formatRelativeTime(execution.createdAt)}>{new Date(execution.createdAt).toLocaleString('en-GB')}</td>
                    <td>{execution.partnerId ? partnersById.get(execution.partnerId)?.name ?? execution.partnerId : 'Unknown partner'}</td>
                    <td>
                      <span className="table-primary">
                        <strong>{flowsById.get(execution.flowId)?.name ?? execution.flowId}</strong>
                        <span className="mono" title={execution.correlationId ?? execution.id}>{shortRuntimeId(execution.correlationId ?? execution.id)}</span>
                      </span>
                    </td>
                    <td><StatusBadge value={execution.status} /></td>
                    <td><Link className="table-action-link" to={{ pathname: '/executions', search: withSearchParam(search, 'correlationId', execution.correlationId ?? execution.id) }}>View details</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : executionsRequest.isLoading && !executionData ? <LoadingState label="Loading recent executions..." />
            : executionsRequest.error ? <ErrorState message="Recent executions could not be loaded." onRetry={() => void executionsRequest.refresh()} />
            : <EmptyState label="No flow executions have been recorded yet." />}
        </section>
      </section>
    </div>
  );
}

export function PartnerOnboardingWizardPage() {
  const { search } = useLocation();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState(initialOnboardingDraft);
  const [created, setCreated] = useState<PartnerRecord | null>(null);
  const [createdFlow, setCreatedFlow] = useState<FlowRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const stepError = validateOnboardingStep(step, draft);

  async function createPartnerSubscription() {
    setIsSubmitting(true);
    setError(null);
    try {
      const partner = await configApi.partners.create({
        name: draft.name,
        code: draft.code,
        protocol: draft.channel === 'SOAP' || draft.channel === 'SFTP' ? 'XML' : 'EDI',
        isActive: draft.status !== 'PAUSED' && draft.status !== 'OFFBOARDED',
        settings: { defaultEndpoint: draft.endpoint, onboardingMode: 'guided' },
        legalName: draft.legalName,
        taxIdentifiers: {},
        contacts: [],
        operationalStatus: draft.status,
        supportedProtocols: [draft.channel],
        inboundCapabilities: [`${draft.channel} inbound`],
        outboundCapabilities: ['canonical outbound'],
        supportedMessageTypes: draft.messageTypes,
        throughputLimits: { requestsPerMinute: draft.requestsPerMinute, payloadMaxMb: draft.payloadMaxMb },
        slaProfile: { retryPolicy: draft.retryPolicy, retentionPolicy: draft.retentionPolicy },
        securityProfile: { schemes: draft.security },
        environmentProfile: draft.environment,
      });
      const flow = await flowsApi.create({
        name: `${draft.code} ${draft.messageTypes[0]} inbound`,
        code: `${draft.code}_${draft.messageTypes[0]}_INBOUND`,
        description: `Draft guided flow for ${draft.name}.`,
        validator: { messageTypes: draft.messageTypes, schemaRequired: true },
        transformer: { mappingRequired: true, canonicalTarget: 'cargo-manifest' },
        router: { routeTo: 'draft-target', protocol: draft.channel },
      }).catch(() => null);
      setCreated(partner);
      setCreatedFlow(flow);
      setStep(onboardingSteps.length);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to create guided partner subscription.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (created) {
    return (
      <div className="page-stack">
        <section className="panel success-panel">
          <span className="status succeeded">Partner created</span>
          <h2>{created.name} is ready for schema and mapping</h2>
          <p>Channel configured for {draft.channel}. A draft integration flow {createdFlow ? `(${createdFlow.code})` : 'can be created from Integration Flows'} is ready for the next step.</p>
          <div className="summary-grid">
            <div><strong>Partner created</strong><span>{created.code}</span></div>
            <div><strong>Channel configured</strong><span>{draft.channel}</span></div>
            <div><strong>Message</strong><span>{draft.messageTypes.join(', ')}</span></div>
            <div><strong>Next action</strong><span>Configure schema / mapping</span></div>
          </div>
          <div className="panel-actions">
            <Link className="button-link primary-link" to={{ pathname: '/schemas', search }}>Configure schema</Link>
            <Link className="button-link" to={{ pathname: '/mapping-designer', search }}>Open mapping designer</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <section className="hero">
        <div>
          <span>PARTNER ONBOARDING</span>
          <h1>Guided Partner Subscription</h1>
          <p>This wizard explains how the partner subscribes, which channel they use, which message they send, and what must be ready before publishing.</p>
        </div>
      </section>
      {error ? <ErrorState message={error} /> : null}
      <WizardProgress steps={onboardingSteps} active={step} />
      <section className="page-grid two-columns">
        <div className="panel">
          {step === 0 ? <PartnerIdentityStep draft={draft} setDraft={setDraft} /> : null}
          {step === 1 ? <ChannelStep draft={draft} setDraft={setDraft} /> : null}
          {step === 2 ? <MessageTypesStep draft={draft} setDraft={setDraft} /> : null}
          {step === 3 ? <SecurityStep draft={draft} setDraft={setDraft} /> : null}
          {step === 4 ? <QuotaStep draft={draft} setDraft={setDraft} /> : null}
          {step === 5 ? <ReviewStep draft={draft} /> : null}
          {stepError ? <p className="form-warning" role="alert">{stepError}</p> : null}
          <div className="form-actions full-width">
            <button type="button" className="ghost-button" disabled={step === 0 || isSubmitting} onClick={() => setStep((current) => current - 1)}>Back</button>
            {step < 5 ? (
              <button type="button" disabled={Boolean(stepError)} onClick={() => setStep((current) => current + 1)}>Continue</button>
            ) : (
              <button type="button" disabled={isSubmitting || Boolean(stepError)} onClick={() => void createPartnerSubscription()}>{isSubmitting ? 'Creating...' : 'Create partner subscription'}</button>
            )}
          </div>
        </div>
        <OnboardingPreview draft={draft} step={step} />
      </section>
    </div>
  );
}

export function MessageSchemaManagerPage() {
  const { search } = useLocation();
  const [sample, setSample] = useState(cuscarSample);
  const [format, setFormat] = useState<'JSON' | 'XML'>('JSON');
  const [messageType, setMessageType] = useState<DocumentType>('CUSCAR');
  const [version, setVersion] = useState('v1');
  const [schemaName, setSchemaName] = useState('io.oss.integration.cuscar.source');
  const [advanced, setAdvanced] = useState(false);
  const [validation, setValidation] = useState<{ status: string; details: string } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const { data, isLoading, error, refresh } = useAsyncData(() => schemasApi.list({ name: '' }), []);

  const schemas = data?.data ?? [];
  const selected = schemas.find((item) => item.id === selectedId) ?? schemas[0] ?? null;
  const fields = useMemo(() => inferFields(sample, format), [format, sample]);

  function validateSample() {
    const fields = inferFields(sample, format);
    setValidation(fields.length
      ? { status: 'PASSED', details: `${fields.length} top-level fields inferred. Sample can be used as a ${messageType} source schema.` }
      : { status: 'FAILED', details: 'The sample could not be parsed. Check JSON/XML syntax before versioning the schema.' });
  }

  async function persistSchema(lifecycleState: 'DRAFT' | 'VALIDATED' = 'DRAFT') {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const created = await schemasApi.create({
        name: schemaName,
        version,
        format: format === 'JSON' ? 'JSON_SCHEMA' : 'XML_SCHEMA',
        compatibilityMode: 'BACKWARD',
        ownerTeam: 'integration',
        domain: 'maritime',
        entityType: messageType,
        contentType: format === 'JSON' ? 'application/json' : 'application/xml',
        schemaDefinition: format === 'JSON' ? buildJsonSchemaFromSample(sample) : sample,
        metadata: {
          messageType,
          samplePayload: sample,
          inferredFields: fields,
          createdFrom: 'oss-integration-ui',
        },
      });
      if (lifecycleState === 'VALIDATED') {
        await schemasApi.transition(created.id, 'VALIDATED', 'Validated from UI sample payload');
      }
      setSelectedId(created.id);
      await refresh();
      setValidation({ status: lifecycleState, details: `Schema ${schemaName} ${version} was persisted.` });
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to persist schema.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function transitionSelected(lifecycleState: 'VALIDATED' | 'APPROVED' | 'ACTIVE') {
    if (!selected) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const updated = await schemasApi.transition(selected.id, lifecycleState, `${lifecycleState.toLowerCase()} from Schema Manager`);
      setSelectedId(updated.id);
      await refresh();
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to transition schema lifecycle.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="EAI Studio"
        title="Schema Manager"
        purpose="Persist governed message schemas, infer fields from sample payloads, and promote schema versions through validation, approval, and activation."
        status="PRODUCTION_READY"
        connected
        next="Publish an active source schema, then create a mapping that references it."
      />
      <StudioWorkflowNav current="schema" search={search} />
      {submitError ? <ErrorState message={submitError} /> : null}
      <section className="page-grid two-columns schema-layout">
        <div className="panel">
          <div className="panel-header">
            <div>
              <h2>Schema catalog</h2>
              <p>Registered backend schemas. Select one to inspect lifecycle and compatibility metadata.</p>
            </div>
          </div>
          {isLoading ? <LoadingState label="Loading schemas..." /> : null}
          {error ? <ErrorState message={error} onRetry={refresh} /> : null}
          {!isLoading && !error && schemas.length === 0 ? <EmptyState label="No schemas registered yet." /> : null}
          <div className="stack-list">
            {schemas.map((schema) => (
              <button
                type="button"
                key={schema.id}
                className={`ops-list-item${selected?.id === schema.id ? ' active' : ''}`}
                onClick={() => setSelectedId(schema.id)}
              >
                <strong>{schema.name}</strong>
                <span>{schema.version} · {schema.format}</span>
                <StatusBadge value={schema.lifecycleState} />
              </button>
            ))}
          </div>
        </div>

        <div className="panel">
          <div className="panel-header">
            <div>
              <h2>Schema detail</h2>
              <p>{selected ? 'Backend persisted schema metadata and lifecycle.' : 'Create or select a schema to inspect details.'}</p>
            </div>
            {selected ? <StatusBadge value={selected.lifecycleState} /> : null}
          </div>
          {selected ? (
            <>
              <SummaryList value={{
                name: selected.name,
                version: selected.version,
                format: selected.format,
                compatibilityMode: selected.compatibilityMode,
                ownerTeam: selected.ownerTeam,
                domain: selected.domain,
                entityType: selected.entityType,
              }} />
              <div className="panel-actions">
                <button type="button" disabled={isSubmitting || selected.lifecycleState !== 'DRAFT'} title={selected.lifecycleState !== 'DRAFT' ? 'Validation requires a draft schema.' : undefined} onClick={() => void transitionSelected('VALIDATED')}>Validate schema</button>
                <button type="button" disabled={isSubmitting || selected.lifecycleState !== 'VALIDATED'} title={selected.lifecycleState !== 'VALIDATED' ? 'Approval requires a validated schema.' : undefined} onClick={() => void transitionSelected('APPROVED')}>Approve schema</button>
                <button
                  type="button"
                  disabled={isSubmitting || selected.lifecycleState !== 'APPROVED'}
                  title={selected.lifecycleState !== 'APPROVED' ? 'Publishing requires an approved schema.' : undefined}
                  onClick={() => setConfirm({
                    title: 'Publish schema version',
                    message: `Activate ${selected.name} ${selected.version}. New mappings can reference this schema as production-ready.`,
                    confirmLabel: 'Publish schema',
                    risk: 'medium',
                    onConfirm: () => transitionSelected('ACTIVE'),
                  })}
                >
                  Publish schema
                </button>
              </div>
              {selected.lifecycleState === 'ACTIVE' ? <div className="next-step-callout"><strong>Schema is ready</strong><span>Continue by creating or updating a mapping against this active schema.</span><Link className="button-link" to={{ pathname: '/mapping-designer', search: appendSearch(search, 'schemaId', selected.id) }}>Continue to Mapping</Link></div> : null}
              <div className="panel-subsection">
                <h3>Compatibility status</h3>
                <p>{selected.compatibilityMode} compatibility. Backend lifecycle state is {selected.lifecycleState}.</p>
              </div>
            </>
          ) : <EmptyState label="No schema selected." />}
        </div>
      </section>

      <section className="page-grid two-columns schema-layout">
        <div className="panel">
          <div className="panel-header">
            <div>
              <h2>Create version from sample</h2>
              <p>Persisted through the schema governance API. Validation changes lifecycle state only after backend confirmation.</p>
            </div>
            <label className="inline-check"><input type="checkbox" checked={advanced} onChange={(event) => setAdvanced(event.target.checked)} /> Advanced JSON editor</label>
          </div>
          <div className="form-grid">
            <label>
              <span>Schema name</span>
              <input value={schemaName} onChange={(event) => setSchemaName(event.target.value)} />
            </label>
            <label>
              <span>Message type</span>
              <select value={messageType} onChange={(event) => setMessageType(event.target.value as DocumentType)}>
                {messageTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
            <label>
              <span>Schema version</span>
              <input value={version} onChange={(event) => setVersion(event.target.value)} />
            </label>
            <label>
              <span>Payload format</span>
              <select value={format} onChange={(event) => setFormat(event.target.value as 'JSON' | 'XML')}>
                <option value="JSON">Sample JSON</option>
                <option value="XML">Sample XML</option>
              </select>
            </label>
            <label>
              <span>Upload schema file</span>
              <input type="file" onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                void file.text().then(setSample);
              }} />
            </label>
            {advanced ? (
              <label className="full-width">
                <span>Example payload</span>
                <textarea rows={18} value={sample} onChange={(event) => setSample(event.target.value)} />
              </label>
            ) : (
              <GuidedPayloadEditor sample={sample} setSample={setSample} format={format} />
            )}
          </div>
          <div className="panel-actions">
            <button type="button" onClick={() => {
              setFormat('JSON');
              setMessageType('CUSCAR');
              setSchemaName('io.oss.integration.cuscar.source');
              setSample(cuscarSample);
            }}>Use CUSCAR sample</button>
            <button type="button" onClick={validateSample}>Validate sample payload</button>
            <button type="button" onClick={() => setVersion(nextVersion(version))}>Version schema</button>
            <button type="button" disabled={isSubmitting} onClick={() => void persistSchema('DRAFT')}>Create draft schema</button>
            <button type="button" disabled={isSubmitting} onClick={() => void persistSchema('VALIDATED')}>Create and validate</button>
          </div>
          {validation ? <div className={`state-card ${validation.status === 'FAILED' ? 'error-state' : ''}`}><strong>{validation.status}</strong><p>{validation.details}</p></div> : null}
        </div>
        <div className="panel">
          <div className="panel-header">
            <div>
              <h2>Field tree</h2>
              <p>Required fields are inferred from the current sample and persisted in schema metadata for designer guidance.</p>
            </div>
            <StatusBadge value={validation?.status === 'PASSED' ? 'VALIDATED' : 'DRAFT'} />
          </div>
          <SchemaTree nodes={fields} />
        </div>
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

export function MappingDesignerPage() {
  const { search } = useLocation();
  const [selectedMappingId, setSelectedMappingId] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [versions, setVersions] = useState<MappingVersionRecord[]>([]);
  const [editorValue, setEditorValue] = useState(JSON.stringify(buildMappingRuleDefinition(defaultMappingRules), null, 2));
  const [changeLog, setChangeLog] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const [showRuleHelp, setShowRuleHelp] = useState(false);
  const canEdit = usePermission('flow.operations.execute');
  const canApprove = usePermission('flow.operations.execute');
  const canActivate = usePermission('flow.operations.execute');
  const { data: mappingData, isLoading, error, refresh } = useAsyncData(() => mappingsApi.list({ page: 1, pageSize: 50 }), []);
  const mappings = mappingData?.data ?? [];
  const selectedMapping = mappings.find((mapping) => mapping.id === selectedMappingId) ?? mappings[0] ?? null;
  const selectedVersion = versions.find((version) => version.id === selectedVersionId) ?? versions[0] ?? null;
  // Bug 6 fix: ACTIVE versions are read-only; drafts/approved versions are editable regardless of currentVersionId
  const readonlyVersion = Boolean(selectedVersion && selectedVersion.lifecycleState === 'ACTIVE');
  const editorJsonError = useMemo(() => {
    try { JSON.parse(editorValue || '{}'); return ''; }
    catch (caught) { return caught instanceof Error ? caught.message : 'Invalid JSON rule definition.'; }
  }, [editorValue]);

  useEffect(() => {
    if (!selectedMappingId && mappings[0]) setSelectedMappingId(mappings[0].id);
  }, [mappings, selectedMappingId]);

  useEffect(() => {
    if (!selectedMappingId) return;
    let cancelled = false;
    mappingsApi.listMappingVersions(selectedMappingId)
      .then((items) => {
        if (cancelled) return;
        setVersions(items);
        const preferred = items.find((item) => item.lifecycleState === 'ACTIVE') ?? items[0] ?? null;
        setSelectedVersionId(preferred?.id ?? '');
        setEditorValue(JSON.stringify(preferred?.ruleDefinition ?? {}, null, 2));
        setChangeLog('');
      })
      .catch((caught) => setSubmitError(caught instanceof Error ? caught.message : 'Unable to load mapping versions.'));
    return () => {
      cancelled = true;
    };
  }, [selectedMappingId]);

  function switchVersion(versionId: string) {
    const version = versions.find((item) => item.id === versionId);
    setSelectedVersionId(versionId);
    setEditorValue(JSON.stringify(version?.ruleDefinition ?? {}, null, 2));
    setChangeLog('');
  }

  async function reloadVersions(mappingId = selectedMappingId, preferDraft = false) {
    if (!mappingId) return;
    const items = await mappingsApi.listMappingVersions(mappingId);
    setVersions(items);
    const preferred = (preferDraft ? items.find((item) => item.lifecycleState === 'DRAFT') : items.find((item) => item.lifecycleState === 'ACTIVE')) ?? items[0] ?? null;
    setSelectedVersionId(preferred?.id ?? '');
    setEditorValue(JSON.stringify(preferred?.ruleDefinition ?? {}, null, 2));
  }

  async function saveNewVersion() {
    if (!selectedMapping) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const ruleDefinition = JSON.parse(editorValue || '{}') as Record<string, unknown>;
      await mappingsApi.createMappingVersion(selectedMapping.id, {
        ruleDefinition,
        sourceSchemaRef: selectedVersion?.sourceSchemaRef ?? selectedMapping.sourceSchemaRef,
        versionLabel: nextVersion(selectedVersion?.versionLabel ?? selectedMapping.version),
        changeLog,
      });
      await reloadVersions(selectedMapping.id, true);
      await refresh();
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to save mapping version.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function transition(action: 'validate' | 'approve' | 'activate') {
    if (!selectedMapping || !selectedVersion) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await mappingsApi.updateMappingVersionLifecycle(selectedMapping.id, selectedVersion.id, action);
      await reloadVersions(selectedMapping.id);
      await refresh();
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to update mapping lifecycle.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="EAI Studio"
        title="Source to Canonical Mapping"
        purpose="Edit persisted mapping versions and promote governed mappings through validation, approval, and activation."
        status="PRODUCTION_READY"
        connected
        next="Run a flow dry-run in Test Lab with the selected mapping version before activating production traffic."
      />
      <StudioWorkflowNav current="mapping" search={search} />
      {submitError ? <ErrorState message={submitError} /> : null}
      <section className="page-grid two-columns">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Mapping catalog</h2>
              <p>Backend mapping definitions and selected active versions.</p>
            </div>
          </div>
          {isLoading ? <LoadingState label="Loading mappings..." /> : null}
          {error ? <ErrorState message={error} onRetry={refresh} /> : null}
          {!isLoading && mappings.length === 0 ? <EmptyState label="No mappings have been created yet." /> : null}
          <div className="stack-list">
            {mappings.map((mapping) => (
              <button type="button" key={mapping.id} className={`ops-list-item${selectedMapping?.id === mapping.id ? ' active' : ''}`} onClick={() => setSelectedMappingId(mapping.id)}>
                <strong>{mapping.name}</strong>
                <span>{mapping.documentType} / {mapping.version}</span>
                <StatusBadge value={mapping.lifecycleState ?? (mapping.isActive ? 'ACTIVE' : 'DRAFT')} />
              </button>
            ))}
          </div>
        </section>
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Version history</h2>
              <p>Versions are loaded from the governed catalog API.</p>
            </div>
            {selectedVersion ? <StatusBadge value={selectedVersion.lifecycleState} /> : null}
          </div>
          {versions.length === 0 ? <EmptyState label="Select a mapping to see versions." /> : (
            <table className="data-table compact-table mapping-version-table">
              <thead><tr><th>Version</th><th>State</th><th>Updated</th><th /></tr></thead>
              <tbody>{versions.map((item) => (
                <tr key={item.id}>
                  <td data-label="Version">{item.versionLabel}</td>
                  <td data-label="State"><StatusBadge value={item.lifecycleState} /></td>
                  <td data-label="Updated">{new Date(item.updatedAt).toLocaleString('en-GB')}</td>
                  <td data-label="Action"><button type="button" onClick={() => switchVersion(item.id)}>View</button></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </section>
      </section>
      <section className="panel">
        <div className="form-grid">
          <label><span>Mapping</span><input value={selectedMapping?.name ?? ''} readOnly /></label>
          <label><span>Document type</span><input value={selectedMapping?.documentType ?? ''} readOnly /></label>
          <label><span>Version</span><select value={selectedVersionId} onChange={(event) => switchVersion(event.target.value)}>{versions.map((version) => <option key={version.id} value={version.id}>{version.versionLabel} / {version.lifecycleState}</option>)}</select></label>
          <label><span>Change log</span><input value={changeLog} readOnly={readonlyVersion} onChange={(event) => setChangeLog(event.target.value)} placeholder={readonlyVersion ? 'Historical version' : 'Describe this new version'} /></label>
          <label className="full-width">
            <span className="label-with-help">
              Rule definition JSON
              <button
                type="button"
                className="help-icon"
                aria-label="Rule definition format reference"
                title="Show format reference"
                onClick={() => setShowRuleHelp((v) => !v)}
              >?</button>
            </span>
            <textarea rows={18} value={editorValue} readOnly={!canEdit || readonlyVersion} onChange={(event) => setEditorValue(event.target.value)} />
          </label>
          {showRuleHelp ? (
            <div className="rule-help-callout full-width">
              <h3>Rule definition formats</h3>
              <p>Three formats are supported. Pick whichever suits the complexity of the mapping.</p>

              <h4>Format 1 — <code>fields</code> + <code>assign</code> &nbsp;<em>simplest, recommended for hand-written rules</em></h4>
              <p><code>assign</code> writes hardcoded values. <code>fields</code> maps <em>source dot-path → target dot-path</em>. Both run together.</p>
              <pre>{`{
  "assign": {
    "canonical.messageType": "CUSCAR",
    "canonical.source":      "OSS Integration"
  },
  "fields": {
    "canonical.vessel.imo":                "vessel.imo",
    "canonical.vessel.name":               "vessel.name",
    "canonical.cargoManifest.reference":   "manifestNo",
    "canonical.cargoManifest.portOfLoad":  "portOfLoading"
  }
}`}</pre>

              <h4>Format 2 — <code>fieldMappings</code> array &nbsp;<em>supports per-field defaults and required guards</em></h4>
              <p>Omit <code>sourcePath</code> to write <code>defaultValue</code> directly. Set <code>required: true</code> to throw if the source value is missing.</p>
              <pre>{`{
  "fieldMappings": [
    { "sourcePath": "vessel.imo",    "targetPath": "canonical.vessel.imo",              "required": true  },
    { "sourcePath": "manifestNo",    "targetPath": "canonical.cargoManifest.reference", "required": true  },
    { "sourcePath": "portOfLoading", "targetPath": "canonical.cargoManifest.portOfLoad","defaultValue": "UNKNOWN" },
    {                                "targetPath": "canonical.messageType",             "defaultValue": "CUSCAR"  }
  ]
}`}</pre>

              <h4>Format 3 — Visual designer format &nbsp;<em>produced automatically when saving from the designer</em></h4>
              <p>Contains executable <code>fieldMappings</code> and a <code>designerRules</code> copy that the UI reloads when you re-open this version.</p>
              <pre>{`{
  "fieldMappings": [
    { "sourcePath": "vessel.imo",  "targetPath": "canonical.vessel.imo" },
    {                              "targetPath": "canonical.messageType", "defaultValue": "CUSCAR" }
  ],
  "designerRules": [
    { "id": "rule-imo",  "type": "direct",   "sourcePath": "vessel.imo", "targetPath": "canonical.vessel.imo",  "expression": "source.vessel.imo" },
    { "id": "rule-type", "type": "constant", "sourcePath": "",           "targetPath": "canonical.messageType", "expression": "\\"CUSCAR\\"" }
  ],
  "updatedFrom": "mapping-designer"
}`}</pre>

              <p style={{ marginTop: 12, color: '#496174' }}>
                <strong>Rule types in designerRules:</strong> <code>direct</code> copies a source path · <code>constant</code> writes a fixed value (quote-wrap strings in expression) · <code>array</code> maps array element paths
              </p>
            </div>
          ) : null}
        </div>
        <div className="panel-actions">
          <button type="button" disabled={isSubmitting || !canEdit || readonlyVersion || !selectedMapping || Boolean(editorJsonError) || !changeLog.trim()} title={!changeLog.trim() ? 'Describe the change before creating a version.' : undefined} onClick={() => void saveNewVersion()}>Save as new version</button>
          <button type="button" disabled={isSubmitting || !canApprove || !selectedVersion || selectedVersion.lifecycleState !== 'DRAFT' || Boolean(editorJsonError)} onClick={() => void transition('validate')}>Validate</button>
          <button type="button" disabled={isSubmitting || !canApprove || !selectedVersion || selectedVersion.lifecycleState !== 'VALIDATED'} onClick={() => void transition('approve')}>Approve</button>
          <button
            type="button"
            disabled={isSubmitting || !canActivate || !selectedVersion || selectedVersion.lifecycleState !== 'APPROVED'}
            onClick={() => setConfirm({
              title: 'Activate mapping version',
              message: 'This activates the selected governed mapping version for runtime flow configuration.',
              confirmLabel: 'Activate mapping',
              risk: 'medium',
              onConfirm: () => transition('activate'),
            })}
          >
            Activate
          </button>
        </div>
        {editorJsonError ? <p className="form-error" role="alert">Rule definition is not valid JSON: {editorJsonError}</p> : <p className="field-success">Rule definition JSON is valid.</p>}
        {selectedVersion?.lifecycleState === 'ACTIVE' ? <div className="next-step-callout"><strong>Mapping is active</strong><span>Run it against a representative payload before publishing a flow.</span><Link className="button-link" to={{ pathname: '/test-lab', search: appendSearch(search, 'mappingVersionId', selectedVersion.id) }}>Continue to Test Lab</Link></div> : null}
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

export function FunctionalTestLabPage() {
  const { search } = useLocation();
  const initialTestParams = useMemo(() => new URLSearchParams(search), [search]);
  const [payload, setPayload] = useState(cuscarSample);
  const [flowId, setFlowId] = useState(() => initialTestParams.get('flowId') ?? '');
  const [mappingVersionId, setMappingVersionId] = useState(() => initialTestParams.get('mappingVersionId') ?? '');
  const [result, setResult] = useState<{ transformedPayload: Record<string, unknown>; validationErrors?: string[] } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const { data: flowData } = useAsyncData(() => flowsApi.list({ page: 1, pageSize: 50 }), []);
  const { data: mappingData } = useAsyncData(() => mappingsApi.list({ page: 1, pageSize: 50, lifecycleState: 'ACTIVE' }), []);
  const flows = flowData?.data ?? [];
  const mappings = mappingData?.data ?? [];
  const payloadError = useMemo(() => {
    try { JSON.parse(payload || '{}'); return ''; }
    catch (caught) { return caught instanceof Error ? caught.message : 'Invalid JSON payload.'; }
  }, [payload]);
  const selectedFlow = flows.find((flow) => flow.id === flowId);
  const validationPassed = Boolean(result && !(result.validationErrors?.length));

  useEffect(() => {
    const params = new URLSearchParams(search);
    const requestedFlowId = params.get('flowId');
    const requestedMappingVersionId = params.get('mappingVersionId');
    if (requestedFlowId) setFlowId(requestedFlowId);
    if (requestedMappingVersionId) setMappingVersionId(requestedMappingVersionId);
    setResult(null);
    setSubmitError(null);
  }, [search]);

  useEffect(() => {
    if (!flowId && flows[0]) setFlowId(flows[0].id);
  }, [flowId, flows]);

  async function runDryRun() {
    if (!flowId) return;
    setIsRunning(true);
    setSubmitError(null);
    try {
      const sourcePayload = JSON.parse(payload || '{}') as Record<string, unknown>;
      setResult(await flowsApi.dryRun(flowId, { sourcePayload, mappingVersionId: mappingVersionId || undefined }));
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to run dry-run.');
    } finally {
      setIsRunning(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="EAI Studio"
        title="Test Before Production"
        purpose="Run a backend dry-run against an existing flow and optional mapping version without persisting an execution."
        status="PRODUCTION_READY"
        connected
        next="Review validation errors and activate the governed version when the transformed payload is correct."
      />
      <StudioWorkflowNav current="test" search={search} />
      {submitError ? <ErrorState message={submitError} /> : null}
      <section className="panel">
        <div className="form-grid">
          <label><span>Flow</span><select value={flowId} onChange={(event) => setFlowId(event.target.value)}><option value="">Select flow</option>{flows.map((flow) => <option key={flow.id} value={flow.id}>{flow.name}</option>)}</select></label>
          {/* Bug 3/4 fix: only expose activeVersionId — never fall back to draft version IDs */}
          <label><span>Mapping version</span><select value={mappingVersionId} onChange={(event) => setMappingVersionId(event.target.value)}><option value="">Flow active mapping</option>{mappings.map((mapping) => <option key={mapping.id} value={mapping.activeVersionId ?? ''} disabled={!mapping.activeVersionId}>{mapping.name} / {mapping.version}{!mapping.activeVersionId ? ' (no active version)' : ''}</option>)}</select></label>
          <label className="full-width"><span>Source payload JSON</span><textarea rows={12} value={payload} onChange={(event) => setPayload(event.target.value)} /></label>
          {payloadError ? <p className="form-error full-width" role="alert">Source payload is not valid JSON: {payloadError}</p> : null}
        </div>
        <div className="panel-actions">
          <button type="button" disabled={!flowId || isRunning || Boolean(payloadError)} onClick={() => void runDryRun()}>{isRunning ? 'Running...' : 'Run dry-run'}</button>
        </div>
        {selectedFlow ? <p className="results-status">Testing {selectedFlow.name} · {mappingVersionId ? 'selected active mapping override' : 'flow active mapping'}. No execution will be persisted.</p> : null}
      </section>
      {result ? (
        <section className="page-grid two-columns">
          <article className="panel">
            <h2>Transformed payload</h2>
            <PayloadViewer value={result.transformedPayload} filename="dry-run-transformed-payload.json" />
          </article>
          <article className="panel">
            <h2>Validation errors</h2>
            {result.validationErrors?.length ? (
              <ul>{result.validationErrors.map((item) => <li key={item}>{item}</li>)}</ul>
            ) : <EmptyState label="No validation errors returned." />}
          </article>
        </section>
      ) : <EmptyState label="Run a dry-run to inspect the transformed payload." />}
      {result ? <div className={`test-readiness ${validationPassed ? 'test-readiness-pass' : 'test-readiness-fail'}`} role="status"><div><strong>{validationPassed ? 'Ready for publishing review' : 'Not ready to publish'}</strong><span>{validationPassed ? 'The dry-run completed without validation errors.' : `${result.validationErrors?.length ?? 0} validation issue(s) must be resolved.`}</span></div>{validationPassed ? <Link className="button-link primary-link" to={{ pathname: '/flows/publish', search: appendSearch(search, 'flowId', flowId) }}>Continue to Publish</Link> : null}</div> : null}
    </div>
  );
}

export function IntegrationFlowBuilderPage() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const restoredDraft = useMemo(readFlowBuilderDraft, []);
  const [draftWasRestored, setDraftWasRestored] = useState(Boolean(restoredDraft));
  const [step, setStep] = useState(restoredDraft?.step ?? 0);
  const [stepTouched, setStepTouched] = useState(false);
  const [createdFlow, setCreatedFlow] = useState<FlowRecord | null>(restoredDraft?.createdFlow ?? null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const { data: partnerData } = useAsyncData(() => configApi.partners.list({ page: 1, pageSize: 100 }), []);
  const { data: schemaData } = useAsyncData(() => schemasApi.list({ lifecycleState: 'ACTIVE' }), []);
  const { data: mappingData } = useAsyncData(() => mappingsApi.list({ page: 1, pageSize: 100, lifecycleState: 'ACTIVE' }), []);
  const { data: connectorData } = useAsyncData(() => configApi.connectorInstances.list({ page: 1, pageSize: 100, isActive: true }), []);
  const canCreateFlow = usePermission('flow.operations.execute');
  const canDeployFlow = usePermission('flow.operations.execute');
  const [config, setConfig] = useState<FlowBuilderConfig>(restoredDraft?.config ?? {
    partner: '',
    protocol: 'REST' as ConnectorProtocolFamily,
    endpoint: '',
    mode: 'Async',
    messageType: MARITIME_DOCUMENT_TYPE_OPTIONS[0] as DocumentType,
    schema: '',
    mapping: '',
    target: 'OSS internal app' as TargetSystem,
    retry: '3',
    dlq: true,
    replay: true,
    timeout: '30',
    idempotencyKey: 'correlationId',
    testPayload: cuscarSample,
    autoDeliver: false,
  });
  const stepError = validateFlowBuilderStep(step, config);

  useEffect(() => {
    try {
      if (config.partner || config.endpoint || step > 0 || createdFlow) {
        sessionStorage.setItem(FLOW_BUILDER_DRAFT_KEY, JSON.stringify({ step, config, createdFlow }));
      } else {
        sessionStorage.removeItem(FLOW_BUILDER_DRAFT_KEY);
      }
    } catch { /* Storage may be unavailable in hardened browser contexts. */ }
  }, [config, createdFlow, step]);

  function goToStep(nextStep: number) {
    setStep(Math.max(0, Math.min(5, nextStep)));
    setStepTouched(false);
  }

  function clearDraft() {
    try { sessionStorage.removeItem(FLOW_BUILDER_DRAFT_KEY); } catch { /* ignore */ }
    setConfig(defaultFlowBuilderConfig());
    setStep(0);
    setStepTouched(false);
    setCreatedFlow(null);
    setDraftWasRestored(false);
  }

  async function saveDraftFlow() {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const saved = createdFlow
        ? await flowsApi.createVersion(createdFlow.id, buildFlowPayload(config))
        : await flowsApi.create(buildFlowPayload(config));
      setCreatedFlow(saved);
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to save flow draft.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function publishFlow() {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      let draft = createdFlow;
      if (!draft) {
        draft = await flowsApi.create(buildFlowPayload(config));
        setCreatedFlow(draft);
      }
      let approved = draft;
      if (draft.selectedVersion.lifecycleState === 'DRAFT') {
        approved = await flowsApi.approve(draft.id, draft.selectedVersion.id);
        setCreatedFlow(approved);
      }
      const active = approved.selectedVersion.lifecycleState === 'APPROVED'
        ? await flowsApi.activate(approved.id, approved.selectedVersion.id)
        : approved;
      setCreatedFlow(active);
      try { sessionStorage.removeItem(FLOW_BUILDER_DRAFT_KEY); } catch { /* ignore */ }
      navigate({ pathname: '/operations/flows', search });
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to publish flow.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (createdFlow?.selectedVersion.lifecycleState === 'ACTIVE') {
    return (
      <section className="panel success-panel">
        <StatusBadge value="PUBLISHED" />
        <h2>{createdFlow.name} is active</h2>
        <p>Backend flow version {createdFlow.selectedVersion.versionLabel} is active. Monitor runtime executions from the operations console.</p>
        <div className="panel-actions">
          <Link className="button-link primary-link" to={{ pathname: '/executions', search }}>Monitor executions</Link>
          <Link className="button-link" to={{ pathname: '/dlq', search }}>Open DLQ & Replay</Link>
        </div>
      </section>
    );
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Guided setup"
        title="Set up integration"
        purpose="Create the operational path for one partner message: where it comes from, how it is understood, where it goes, and how failures are handled."
        status="PRODUCTION_READY"
        connected
        next="Complete the steps in order. The builder will point to the exact setup page when a required item is missing."
        trustDetail="Builder capability is production ready and connected. This flow remains a draft until you explicitly publish it."
      />
      {draftWasRestored ? <div className="draft-restored" role="status"><span>Recovered your in-progress setup from this browser session.</span><button type="button" className="ghost-button" onClick={clearDraft}>Start over</button></div> : null}
      {submitError ? <ErrorState message={submitError} /> : null}
      <WizardProgress steps={flowSteps} active={step} onSelect={goToStep} />
      <section className="page-grid two-columns">
        <div className="panel" onChangeCapture={() => setStepTouched(true)}>
          <ExtractedFlowBuilderStep
            step={step}
            config={config}
            setConfig={setConfig}
            partners={partnerData?.data ?? []}
            schemas={schemaData?.data ?? []}
            mappings={mappingData?.data ?? []}
            connectors={connectorData?.data ?? []}
            messageTypes={messageTypes}
            protocols={protocols}
            targets={targets}
            samplePayload={cuscarSample}
          />
          {stepTouched && stepError ? <p className="form-error" role="alert">{stepError}</p> : null}
          <div className="form-actions">
            <button className="ghost-button" type="button" disabled={step === 0} onClick={() => goToStep(step - 1)}>Back</button>
            {step < 5 ? <button type="button" disabled={Boolean(stepError)} onClick={() => goToStep(step + 1)}>Continue</button> : (
              <>
                <button type="button" disabled={isSubmitting || !canCreateFlow || Boolean(stepError)} onClick={() => void saveDraftFlow()}>Save draft flow</button>
                <button
                  type="button"
                  disabled={isSubmitting || !canDeployFlow || Boolean(stepError)}
                  onClick={() => setConfirm({
                    title: 'Publish integration flow',
                    message: 'This approves and activates the backend flow version. Runtime traffic can use the active configuration.',
                    confirmLabel: 'Publish flow',
                    risk: 'high',
                    requireText: 'PUBLISH',
                    onConfirm: publishFlow,
                  })}
                >
                  Publish flow
                </button>
              </>
            )}
          </div>
        </div>
        <div className="panel">
          <h2>Flow summary</h2>
          <FlowBuilderSummary config={config} partners={partnerData?.data ?? []} schemas={schemaData?.data ?? []} mappings={mappingData?.data ?? []} connectors={connectorData?.data ?? []} />
          {createdFlow ? <div className="state-card"><strong>Persisted draft</strong><p>{createdFlow.name} · {createdFlow.selectedVersion.lifecycleState}</p></div> : null}
        </div>
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function publishCenterTransitionDisabledReason(flow: FlowRecord, action: 'approve' | 'activate' | 'rollback'): string | null {
  const state = flow.selectedVersion.lifecycleState;
  const readinessIssues = flowPublishReadinessIssues(flow);
  if ((action === 'approve' || action === 'activate') && readinessIssues.length) return readinessIssues[0];
  if (action === 'approve' && state !== 'DRAFT') return `Cannot deploy: version is already ${state}.`;
  if (action === 'activate' && state !== 'APPROVED') return `Cannot activate: version is ${state}, expected APPROVED.`;
  if (action === 'rollback' && state !== 'ACTIVE') return `Cannot roll back: version is ${state}, expected ACTIVE.`;
  return null;
}

export function PublishCenterPage() {
  const { search } = useLocation();
  const requestedFlowId = new URLSearchParams(search).get('flowId');
  const { data, isLoading, error, refresh } = useAsyncData(() => flowsApi.list({ page: 1, pageSize: 100 }), []);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState<'ALL' | 'DRAFT' | 'APPROVED' | 'ACTIVE'>('ALL');
  const flows = (data?.data ?? []).filter((flow) => (stateFilter === 'ALL' || flow.selectedVersion.lifecycleState === stateFilter) && (!requestedFlowId || flow.id === requestedFlowId));

  async function transition(flow: FlowRecord, action: 'approve' | 'activate' | 'rollback') {
    setActionError(null);
    try {
      if (action === 'approve') await flowsApi.approve(flow.id, flow.selectedVersion.id);
      if (action === 'activate') await flowsApi.activate(flow.id, flow.selectedVersion.id);
      if (action === 'rollback') await flowsApi.rollback(flow.id, flow.selectedVersion.id);
      await refresh();
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to update flow lifecycle.');
    }
  }

  function requestTransition(flow: FlowRecord, action: 'approve' | 'activate' | 'rollback') {
    if (publishCenterTransitionDisabledReason(flow, action)) return;
    if (action === 'approve') {
      setConfirm({
        title: 'Deploy flow',
        message: `This approves ${flow.name} version ${flow.selectedVersion.versionLabel} for activation.`,
        confirmLabel: 'Deploy',
        risk: 'medium',
        onConfirm: () => transition(flow, 'approve'),
      });
      return;
    }
    setConfirm({
      title: action === 'activate' ? 'Activate flow' : 'Rollback flow',
      message: action === 'activate'
        ? `Activate ${flow.name} version ${flow.selectedVersion.versionLabel}. Runtime traffic can use this version immediately.`
        : `Rollback ${flow.name} to a prior version. This deactivates the current version ${flow.selectedVersion.versionLabel}.`,
      confirmLabel: action === 'activate' ? 'Activate' : 'Rollback',
      risk: 'high',
      requireText: action === 'activate' ? 'ACTIVATE' : 'ROLLBACK',
      onConfirm: () => transition(flow, action),
    });
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="EAI Studio"
        title="Publish Center"
        purpose="Review persisted flow versions and move them through approval, activation, and rollback using backend lifecycle APIs."
        status="PRODUCTION_READY"
        connected
        next="After activation, monitor executions and exchanges from Operations."
      />
      <StudioWorkflowNav current="publish" search={search} />
      {actionError ? <ErrorState message={actionError} /> : null}
      <section className="panel">
        <div className="panel-header"><div><h2>Release candidates</h2><p>Review configuration blockers before approving or activating runtime traffic.</p></div><div className="status-chip-row" aria-label="Flow lifecycle filter">{(['ALL', 'DRAFT', 'APPROVED', 'ACTIVE'] as const).map((state) => <button type="button" key={state} className={`status-chip${stateFilter === state ? ' active' : ''}`} aria-pressed={stateFilter === state} onClick={() => setStateFilter(state)}>{state === 'ALL' ? 'All flows' : state}</button>)}</div></div>
        {isLoading ? <LoadingState label="Loading flows..." /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        {!isLoading && !error && !flows.length ? <EmptyState label="No flows matched the current publication filter." /> : null}
        {flows.length ? (
          <div className="table-scroll"><table className="data-table publish-table">
            <thead><tr><th>Flow</th><th>Version</th><th>State</th><th>Readiness</th><th>Configuration</th><th>Actions</th></tr></thead>
            <tbody>{flows.map((flow) => {
              const issues = flowPublishReadinessIssues(flow);
              return (
              <tr key={flow.id}>
                <td data-label="Flow"><div className="table-primary"><strong>{flow.name}</strong><span>{flow.code}</span></div></td>
                <td data-label="Version">{flow.selectedVersion.versionLabel}</td>
                <td data-label="State"><StatusBadge value={flow.selectedVersion.lifecycleState} /></td>
                <td data-label="Readiness"><StatusBadge value={issues.length ? 'BLOCKED' : 'READY'} />{issues.length ? <small className="execution-secondary">{issues.join(' ')}</small> : <small className="execution-secondary">Configuration checks passed. Run a dry-run before activation.</small>}</td>
                <td data-label="Configuration"><span>{flow.selectedVersion.mappingVersionId ? 'Mapping attached' : 'No mapping'}</span><small className="execution-secondary">{flow.selectedVersion.autoDeliver ? (flow.selectedVersion.connectorInstanceId || flow.selectedVersion.connectorVersion ? 'Delivery endpoint attached' : 'Delivery endpoint missing') : 'Automatic delivery off'}</small></td>
                <td data-label="Actions" className="actions-cell publish-actions">
                  <Link className="button-link" to={{ pathname: '/test-lab', search: appendSearch(search, 'flowId', flow.id) }}>Test</Link>
                  <DisabledActionButton reason={publishCenterTransitionDisabledReason(flow, 'approve')} onClick={() => requestTransition(flow, 'approve')}>Approve</DisabledActionButton>
                  <DisabledActionButton reason={publishCenterTransitionDisabledReason(flow, 'activate')} onClick={() => requestTransition(flow, 'activate')}>Activate</DisabledActionButton>
                  <DisabledActionButton className="danger-link" reason={publishCenterTransitionDisabledReason(flow, 'rollback')} onClick={() => requestTransition(flow, 'rollback')}>Rollback</DisabledActionButton>
                </td>
              </tr>);
            })}</tbody>
          </table></div>
        ) : null}
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

export function DlqReplayPage() {
  return <ExchangesPage recoveryMode />;
}

type StudioWorkflowStage = 'schema' | 'mapping' | 'test' | 'publish';

const studioWorkflowStages: Array<{ id: StudioWorkflowStage; label: string; detail: string; pathname: string }> = [
  { id: 'schema', label: '1. Schema', detail: 'Define the contract', pathname: '/schemas' },
  { id: 'mapping', label: '2. Mapping', detail: 'Transform the payload', pathname: '/mapping-designer' },
  { id: 'test', label: '3. Test', detail: 'Prove representative data', pathname: '/test-lab' },
  { id: 'publish', label: '4. Publish', detail: 'Review and release', pathname: '/flows/publish' },
];

function StudioWorkflowNav({ current, search }: { current: StudioWorkflowStage; search: string }) {
  return (
    <nav className="studio-workflow" aria-label="Integration build and release workflow">
      {studioWorkflowStages.map((stage) => (
        <Link
          key={stage.id}
          className={stage.id === current ? 'studio-workflow-step active' : 'studio-workflow-step'}
          aria-current={stage.id === current ? 'step' : undefined}
          to={{ pathname: stage.pathname, search }}
        >
          <strong>{stage.label}</strong>
          <span>{stage.detail}</span>
        </Link>
      ))}
    </nav>
  );
}

function appendSearch(search: string, key: string, value: string): string {
  const params = new URLSearchParams(search);
  params.set(key, value);
  const query = params.toString();
  return query ? `?${query}` : '';
}

function flowPublishReadinessIssues(flow: FlowRecord): string[] {
  const version = flow.selectedVersion;
  const issues: string[] = [];
  if (!version.steps.length) issues.push('Flow has no runtime steps.');
  if (version.autoDeliver && !version.connectorInstanceId && !version.connectorVersion) issues.push('Automatic delivery requires a delivery endpoint.');
  if (!version.retryPolicy || Object.keys(version.retryPolicy).length === 0) issues.push('Retry policy is missing.');
  if (!version.dlqPolicy || Object.keys(version.dlqPolicy).length === 0) issues.push('Recovery policy is missing.');
  return issues;
}

export function ConnectorsConsolePage() {
  const [tab, setTab] = useState<'instances' | 'definitions'>('instances');
  const { data, isLoading, error, refresh } = useAsyncData(() => configApi.connectorInstances.list({ page: 1, pageSize: 100 }), []);
  const connectors = data?.data ?? [];
  const activeConnectors = connectors.filter((connector) => connector.isActive).length;
  const unhealthyConnectors = connectors.filter((connector) => ['DEGRADED', 'UNHEALTHY', 'DISABLED'].includes(connector.health.status)).length;

  return (
    <div className="page-stack">
      <section className="hero">
        <div>
          <span>DELIVERY CHANNELS</span>
          <h1>Delivery Channels</h1>
          <p>Manage the real endpoints integrations use to deliver messages. The technical connector catalog is still available for administrators.</p>
        </div>
        <div className="panel-actions">
          <button type="button" aria-pressed={tab === 'instances'} className={tab === 'instances' ? 'primary-link' : 'ghost-button'} onClick={() => setTab('instances')}>Delivery endpoints</button>
          <button type="button" aria-pressed={tab === 'definitions'} className={tab === 'definitions' ? 'primary-link' : 'ghost-button'} onClick={() => setTab('definitions')}>Technical catalog</button>
        </div>
      </section>
      <section className="panel">
        {isLoading ? <LoadingState label="Loading connectors..." /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        <div className="connector-grid">
          <article className="operations-card"><span className="operations-card-label">Active</span><strong>{activeConnectors}</strong><p>Delivery endpoints available to active integrations.</p></article>
          <article className="operations-card"><span className="operations-card-label">Needs attention</span><strong>{unhealthyConnectors}</strong><p>Endpoints that are degraded, unhealthy, or disabled.</p></article>
          {protocols.map((protocol) => {
            const count = connectors.filter((item) => item.protocolFamily === protocol).length;
            return <article className="operations-card" key={protocol}><span className="operations-card-label">{protocol}</span><strong>{count}</strong><p>{protocolDescription(protocol)}</p></article>;
          })}
        </div>
      </section>
      <div className="tabs" aria-label="Connector administration views">
        <button type="button" aria-pressed={tab === 'instances'} className={tab === 'instances' ? 'active' : ''} onClick={() => setTab('instances')}>Delivery endpoints</button>
        <button type="button" aria-pressed={tab === 'definitions'} className={tab === 'definitions' ? 'active' : ''} onClick={() => setTab('definitions')}>Technical catalog</button>
      </div>
      {tab === 'instances' ? <ConnectorInstancesPage /> : <ConnectorDefinitionsPage />}
    </div>
  );
}

export function CuscarDemoFlowPage() {
  const result = buildTestResult(cuscarSample, 'JSON', 'PMIS', 'full');
  return (
    <div className="page-stack">
      <section className="hero">
        <div>
          <span>DEV DEMO</span>
          <h1>CUSCAR Guided Demo Flow</h1>
          <p>A complete example showing the sample CUSCAR JSON, inferred source schema, canonical cargo manifest target, default mapping rules, test case, and expected canonical output.</p>
        </div>
      </section>
      <section className="page-grid two-columns">
        <div className="panel">
          <h2>Sample CUSCAR JSON</h2>
          <PayloadViewer value={cuscarSample} contentType="application/json" filename="cuscar-sample.json" />
        </div>
        <div className="panel">
          <h2>Source schema</h2>
          <SchemaTree nodes={inferFields(cuscarSample, 'JSON')} compact />
        </div>
      </section>
      <section className="page-grid two-columns">
        <div className="panel">
          <h2>Default mapping rules</h2>
          <div className="rule-list">
            {defaultMappingRules.map((rule) => <div className="rule-row" key={rule.id}><span>{rule.sourcePath || '[constant]'}</span><strong>-&gt;</strong><span>{rule.targetPath}</span><StatusBadge value={rule.type} /></div>)}
          </div>
        </div>
        <div className="panel">
          <h2>Expected canonical output</h2>
          <PayloadViewer value={result.canonicalPayload} filename="cuscar-canonical-payload.json" />
        </div>
      </section>
      <TestResultGrid result={result} input={cuscarSample} />
    </div>
  );
}

function PartnerIdentityStep({ draft, setDraft }: { draft: OnboardingDraft; setDraft: React.Dispatch<React.SetStateAction<OnboardingDraft>> }) {
  return (
    <>
      <PanelIntro title="Step 1: Partner identity" detail="Who is subscribing, in which environment, and what lifecycle state should they start with?" />
      <div className="form-grid">
        <TextField label="Partner name" value={draft.name} onChange={(name) => setDraft((current) => ({ ...current, name }))} />
        <TextField label="Code" value={draft.code} onChange={(code) => setDraft((current) => ({ ...current, code }))} />
        <TextField label="Legal name" value={draft.legalName} onChange={(legalName) => setDraft((current) => ({ ...current, legalName }))} />
        <SelectField label="Environment" value={draft.environment} options={['DEV', 'TEST', 'UAT', 'PROD']} onChange={(environment) => setDraft((current) => ({ ...current, environment: environment as Environment }))} />
        <SelectField label="Status" value={draft.status} options={['ONBOARDING', 'OPERATIONAL', 'PAUSED', 'OFFBOARDED']} onChange={(status) => setDraft((current) => ({ ...current, status: status as PartnerStatus }))} />
      </div>
    </>
  );
}

function ChannelStep({ draft, setDraft }: { draft: OnboardingDraft; setDraft: React.Dispatch<React.SetStateAction<OnboardingDraft>> }) {
  return (
    <>
      <PanelIntro title="Step 2: Integration channel" detail="Choose how the partner will send or test messages. The endpoint/topic/folder becomes the subscription entry point." />
      <ChoiceGrid values={protocols} selected={[draft.channel]} onToggle={(channel) => setDraft((current) => ({ ...current, channel: channel as ConnectorProtocolFamily }))} single />
      <div className="form-grid"><TextField label="Endpoint / topic / folder" value={draft.endpoint} onChange={(endpoint) => setDraft((current) => ({ ...current, endpoint }))} /></div>
    </>
  );
}

function MessageTypesStep({ draft, setDraft }: { draft: OnboardingDraft; setDraft: React.Dispatch<React.SetStateAction<OnboardingDraft>> }) {
  return (
    <>
      <PanelIntro title="Step 3: Message types" detail="Select the maritime or custom messages this subscription accepts." />
      <ChoiceGrid values={messageTypes} selected={draft.messageTypes} onToggle={(type) => setDraft((current) => ({ ...current, messageTypes: toggle(current.messageTypes, type as DocumentType) }))} />
    </>
  );
}

function SecurityStep({ draft, setDraft }: { draft: OnboardingDraft; setDraft: React.Dispatch<React.SetStateAction<OnboardingDraft>> }) {
  return (
    <>
      <PanelIntro title="Step 4: Security" detail="Capture the controls required before the partner can send production traffic." />
      <ChoiceGrid values={securitySchemes} selected={draft.security} onToggle={(scheme) => setDraft((current) => ({ ...current, security: toggle(current.security, scheme as EaiSecurityScheme) }))} />
    </>
  );
}

function QuotaStep({ draft, setDraft }: { draft: OnboardingDraft; setDraft: React.Dispatch<React.SetStateAction<OnboardingDraft>> }) {
  return (
    <>
      <PanelIntro title="Step 5: Quotas and limits" detail="Define the runtime guardrails for throughput, payload size, retries, and retention." />
      <div className="form-grid">
        <NumberField label="Requests per minute" value={draft.requestsPerMinute} onChange={(requestsPerMinute) => setDraft((current) => ({ ...current, requestsPerMinute }))} />
        <NumberField label="Payload max size (MB)" value={draft.payloadMaxMb} onChange={(payloadMaxMb) => setDraft((current) => ({ ...current, payloadMaxMb }))} />
        <TextField label="Retry policy" value={draft.retryPolicy} onChange={(retryPolicy) => setDraft((current) => ({ ...current, retryPolicy }))} />
        <TextField label="Retention policy" value={draft.retentionPolicy} onChange={(retentionPolicy) => setDraft((current) => ({ ...current, retentionPolicy }))} />
      </div>
    </>
  );
}

function ReviewStep({ draft }: { draft: OnboardingDraft }) {
  return (
    <>
      <PanelIntro title="Step 6: Review and create" detail="The console will create the partner, default channel metadata, and a draft integration flow using existing APIs." />
      <SummaryList value={draft as unknown as Record<string, unknown>} />
    </>
  );
}

function OnboardingPreview({ draft, step }: { draft: OnboardingDraft; step: number }) {
  const prompts = [
    'The partner subscribes by receiving a unique partner code and a configured channel.',
    `Protocol selected: ${draft.channel}. Configure endpoint/topic/folder before production.`,
    `Message selected: ${draft.messageTypes.join(', ') || 'none yet'}. The next page defines the expected schema.`,
    `Security controls: ${draft.security.join(', ') || 'none selected'}.`,
    `Runtime limits: ${draft.requestsPerMinute}/min and ${draft.payloadMaxMb} MB payloads.`,
    'After create, continue to schema, mapping, test lab, and publish.',
  ];
  return (
    <aside className="panel next-action">
      <h2>What this answers</h2>
      <p>{prompts[step]}</p>
      <div className="stack-list">
        <div className="stack-item"><strong>Subscription</strong><span>{draft.code || 'Partner code pending'}</span></div>
        <div className="stack-item"><strong>Protocol</strong><span>{draft.channel}</span></div>
        <div className="stack-item"><strong>Message</strong><span>{draft.messageTypes.join(', ')}</span></div>
        <div className="stack-item"><strong>Next action</strong><span>{step < 5 ? onboardingSteps[step + 1] : 'Configure schema / mapping'}</span></div>
      </div>
    </aside>
  );
}

function WizardProgress({ steps, active, onSelect }: { steps: string[]; active: number; onSelect?: (step: number) => void }) {
  return (
    <div className="wizard-progress" aria-label="Setup progress">
      {steps.map((item, index) => {
        const content = <><span>{index + 1}</span><strong>{item}</strong></>;
        const className = `wizard-step${index === active ? ' active' : ''}${index < active ? ' complete' : ''}`;
        return onSelect && index <= active
          ? <button type="button" aria-current={index === active ? 'step' : undefined} className={className} key={item} onClick={() => onSelect(index)}>{content}</button>
          : <div aria-current={index === active ? 'step' : undefined} className={className} key={item}>{content}</div>;
      })}
    </div>
  );
}

function PanelIntro({ title, detail }: { title: string; detail: string }) {
  return <div className="panel-header"><div><h2>{title}</h2><p>{detail}</p></div></div>;
}

function ChoiceGrid({ values, selected, onToggle, single = false }: { values: string[]; selected: string[]; onToggle: (value: string) => void; single?: boolean }) {
  return <div className="choice-grid">{values.map((value) => <button aria-pressed={selected.includes(value)} className={`choice-tile${selected.includes(value) ? ' selected' : ''}`} type="button" key={value} onClick={() => onToggle(value)}><strong>{formatLabel(value)}</strong><span>{single ? 'Select this option' : selected.includes(value) ? 'Selected' : 'Add to subscription'}</span></button>)}</div>;
}

function validateOnboardingStep(step: number, draft: OnboardingDraft): string | null {
  if (step === 0 && (!draft.name.trim() || !draft.code.trim() || !draft.legalName.trim())) return 'Enter the partner name, unique code, and legal name to continue.';
  if (step === 1 && !draft.endpoint.trim()) return 'Enter the endpoint, topic, or folder used by this channel.';
  if (step === 2 && draft.messageTypes.length === 0) return 'Select at least one message type.';
  if (step === 3 && draft.security.length === 0) return 'Select at least one security control.';
  if (step === 4 && (draft.requestsPerMinute <= 0 || draft.payloadMaxMb <= 0 || !draft.retryPolicy.trim() || !draft.retentionPolicy.trim())) return 'Set positive throughput and payload limits, plus retry and retention policies.';
  if (step === 5) return [0, 1, 2, 3, 4].map((index) => validateOnboardingStep(index, draft)).find(Boolean) ?? null;
  return null;
}

function validateFlowBuilderStep(step: number, config: FlowBuilderConfig): string | null {
  if (step === 0) {
    if (!config.partner || !config.endpoint.trim()) return 'Select a partner and enter its endpoint, topic, or folder.';
    if (['REST', 'WEBHOOK', 'SOAP'].includes(config.protocol)) {
      try {
        const endpoint = new URL(config.endpoint);
        if (endpoint.protocol !== 'https:') return 'Enter a secure HTTPS endpoint URL.';
      } catch { return 'Enter a valid HTTPS endpoint URL.'; }
    }
    if (config.protocol === 'SFTP' && !config.endpoint.startsWith('/')) return 'Enter an absolute SFTP folder beginning with /.';
    if (config.protocol === 'KAFKA' && !/^[A-Za-z0-9._-]+$/.test(config.endpoint)) return 'Enter a valid Kafka topic using letters, numbers, dots, underscores, or hyphens.';
  }
  if (step === 1) {
    if (!config.messageType || !config.schema) return 'Select a message type and an active schema.';
    try { JSON.parse(config.testPayload); } catch { return 'Enter a valid JSON test payload.'; }
  }
  if (step === 2 && !config.mapping) return 'Select an active mapping version.';
  if (step === 3 && config.autoDeliver && !config.connectorInstanceId) return 'Select a delivery endpoint when automatic delivery is enabled.';
  if (step === 4) {
    const attempts = Number(config.retry);
    const timeoutSeconds = Number(config.timeout);
    if (!Number.isInteger(attempts) || attempts < 1 || attempts > 20) return 'Set maximum attempts between 1 and 20.';
    if (!Number.isFinite(timeoutSeconds) || timeoutSeconds < 1 || timeoutSeconds > 300) return 'Set timeout between 1 and 300 seconds.';
    if (!config.idempotencyKey.trim()) return 'Select an idempotency key policy.';
  }
  if (step === 5) return [0, 1, 2, 3, 4].map((index) => validateFlowBuilderStep(index, config)).find(Boolean) ?? null;
  return null;
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label><span>{label}</span><input value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return <label><span>{label}</span><input type="number" value={value} onChange={(event) => onChange(Number(event.target.value))} /></label>;
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  return <label><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option key={option} value={option}>{formatLabel(option)}</option>)}</select></label>;
}

function SummaryList({ value }: { value: Record<string, unknown> }) {
  return <dl className="detail-list">{Object.entries(value).map(([key, entry]) => <Fragment key={key}><dt>{formatLabel(key)}</dt><dd>{Array.isArray(entry) ? entry.join(', ') : typeof entry === 'object' ? JSON.stringify(entry) : String(entry)}</dd></Fragment>)}</dl>;
}

function SchemaTree({ nodes, compact = false }: { nodes: SchemaFieldNode[]; compact?: boolean }) {
  if (!nodes.length) return <EmptyState label="No fields inferred yet." />;
  return <ul className={`schema-tree${compact ? ' compact' : ''}`}>{nodes.map((node) => <SchemaNode key={node.path} node={node} />)}</ul>;
}

function SchemaNode({ node }: { node: SchemaFieldNode }) {
  return <li><div><strong>{node.name}</strong><span>{node.type}</span><em>{node.required ? 'Required' : 'Optional'}</em></div>{node.children?.length ? <SchemaTree nodes={node.children} compact /> : null}</li>;
}

function TestResultGrid({ result, input }: { result: FunctionalTestResult; input: string }) {
  return (
    <section className="test-result-grid">
      <ResultPanel title="1. Input payload" value={input} />
      <ResultPanel title="2. Validation result" value={JSON.stringify(result.validation, null, 2)} />
      <ResultPanel title="3. Canonical envelope" value={JSON.stringify(result.canonicalEnvelope, null, 2)} />
      <ResultPanel title="4. Canonical payload" value={JSON.stringify(result.canonicalPayload, null, 2)} />
      <ResultPanel title="5. Target transformed payload" value={JSON.stringify(result.targetPayload, null, 2)} />
      <ResultPanel title="6. Delivery simulation result" value={JSON.stringify(result.delivery, null, 2)} />
      <ResultPanel title="7. Errors and warnings" value={JSON.stringify({ errors: result.validation.errors, warnings: result.validation.warnings }, null, 2)} />
      <ResultPanel title="8. Execution trace" value={JSON.stringify(result.trace ?? [], null, 2)} />
      <ResultPanel title="9. Timing / latency" value={JSON.stringify(result.timing ?? {}, null, 2)} />
      <ResultPanel title="10. Persisted test case" value={result.persistedReferenceId ? `Payload reference: ${result.persistedReferenceId}` : 'Not persisted yet.'} />
    </section>
  );
}

function ResultPanel({ title, value }: { title: string; value: string }) {
  return <article className="panel"><h2>{title}</h2><PayloadViewer value={value} contentType="application/json" filename={`${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.txt`} /></article>;
}

function GuidedAction({ title, detail, to, search }: { title: string; detail: string; to: string; search: string }) {
  return <Link className="stack-item action-item" to={{ pathname: to, search }}><strong>{title}</strong><span>{detail}</span></Link>;
}

function formatRelativeTime(value: string): string {
  const elapsedSeconds = Math.round((new Date(value).getTime() - Date.now()) / 1_000);
  const formatter = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });
  if (Math.abs(elapsedSeconds) < 60) return formatter.format(elapsedSeconds, 'second');
  const elapsedMinutes = Math.round(elapsedSeconds / 60);
  if (Math.abs(elapsedMinutes) < 60) return formatter.format(elapsedMinutes, 'minute');
  const elapsedHours = Math.round(elapsedMinutes / 60);
  if (Math.abs(elapsedHours) < 24) return formatter.format(elapsedHours, 'hour');
  return formatter.format(Math.round(elapsedHours / 24), 'day');
}

function shortRuntimeId(value: string): string {
  return value.length > 20 ? `${value.slice(0, 17)}...` : value;
}

function withSearchParam(search: string, key: string, value: string): string {
  const params = new URLSearchParams(search);
  params.set(key, value);
  return `?${params.toString()}`;
}

function Kpi({ label, value, detail, trend }: { label: string; value: string | number; detail: string; trend?: ReactNode }) {
  return <article className="operations-card"><span className="operations-card-label">{label}</span><strong>{value}</strong>{trend}<p>{detail}</p></article>;
}

function buildTestResult(payload: string, format: 'JSON' | 'XML', target: TargetSystem, mode: 'validate' | 'transform' | 'deliver' | 'full', ruleDefinition?: Record<string, unknown>): FunctionalTestResult {
  const parsed = format === 'JSON' ? parseJson(payload) : { xml: payload };
  const errors = parsed ? [] : ['Input payload is not valid JSON/XML for the selected format.'];
  const warnings = mode === 'deliver' ? ['Transformation skipped because only delivery simulation was requested.'] : [];
  const canonicalPayload = parsed ? transformCuscarToCanonical(parsed as Record<string, unknown>) : {};
  const mappingOutput = ruleDefinition ? applyPreviewMapping(parsed as Record<string, unknown>, ruleDefinition) : canonicalPayload;
  return {
    validation: { status: errors.length ? 'FAILED' : 'PASSED', errors, warnings },
    canonicalEnvelope: {
      messageType: 'CUSCAR',
      correlationId: `test-${Date.now()}`,
      partnerCode: readPath(parsed, ['declarant', 'code']) ?? 'UNKNOWN',
      mappingVersion: 'CUSCAR canonical mapping v1',
    },
    canonicalPayload,
    targetPayload: { target, operation: 'upsertCargoManifest', payload: mappingOutput },
    delivery: { status: errors.length ? 'BLOCKED' : 'SIMULATED', target, message: errors.length ? 'Fix validation errors before delivery.' : `No external call made. ${target} delivery simulation passed.` },
  };
}

function buildJsonSchemaFromSample(sample: string): Record<string, unknown> {
  const parsed = parseJson(sample);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { type: 'object', properties: {}, required: [] };
  }
  const properties = Object.fromEntries(Object.entries(parsed).map(([key, value]) => [key, { type: jsonSchemaType(value) }]));
  const required = Object.entries(parsed).filter(([, value]) => value !== null && value !== undefined && value !== '').map(([key]) => key);
  return { type: 'object', properties, required };
}

function jsonSchemaType(value: unknown): string {
  if (Array.isArray(value)) return 'array';
  if (value === null) return 'null';
  if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'number';
  if (typeof value === 'object') return 'object';
  return typeof value;
}

function buildMappingRuleDefinition(rules: MappingRuleDraft[]): Record<string, unknown> {
  return {
    // Bug 1 fix: fieldMappings is the key the backend executor reads
    fieldMappings: rules.map((rule) => ({
      sourcePath: rule.type !== 'constant' && rule.sourcePath ? rule.sourcePath : undefined,
      targetPath: rule.targetPath,
      defaultValue: rule.type === 'constant' ? rule.expression.replace(/^"|"$/g, '') : undefined,
      required: false,
    })),
    // Keep the rich designer representation so the UI can reload it
    designerRules: rules,
    updatedFrom: 'mapping-designer',
  };
}

function readMappingRules(ruleDefinition: Record<string, unknown>): MappingRuleDraft[] {
  // New format: designer rules stored separately from the executable fieldMappings
  const designerRules = ruleDefinition['designerRules'];
  if (Array.isArray(designerRules)) return designerRules as MappingRuleDraft[];
  // Legacy format: rules saved directly under 'rules' key before the fix
  const legacyRules = ruleDefinition['rules'];
  if (Array.isArray(legacyRules)) return legacyRules as MappingRuleDraft[];
  return defaultMappingRules;
}

function applyPreviewMapping(source: Record<string, unknown> | null, ruleDefinition: Record<string, unknown>): Record<string, unknown> {
  if (!source) return {};
  const rules = readMappingRules(ruleDefinition);
  if (!rules.length) return transformCuscarToCanonical(source);
  const output: Record<string, unknown> = {};
  for (const rule of rules) {
    if (!rule.targetPath) continue;
    const value = rule.type === 'constant'
      ? rule.expression.replace(/^"|"$/g, '')
      : readDottedPath(source, rule.sourcePath.replace(/^source\./, ''));
    writeDottedPath(output, rule.targetPath.replace(/^canonical\./, ''), value ?? rule.expression ?? null);
  }
  return output;
}

function readDottedPath(value: Record<string, unknown>, path: string): unknown {
  return path.split('.').filter(Boolean).reduce<unknown>((current, segment) => (
    current && typeof current === 'object' ? (current as Record<string, unknown>)[segment] : undefined
  ), value);
}

function writeDottedPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.').filter(Boolean);
  let cursor = target;
  for (const part of parts.slice(0, -1)) {
    if (!cursor[part] || typeof cursor[part] !== 'object') cursor[part] = {};
    cursor = cursor[part] as Record<string, unknown>;
  }
  if (parts.length) cursor[parts[parts.length - 1]] = value;
}

function buildFlowPayload(config: Record<string, unknown>): Record<string, unknown> {
  const code = `${String(config.partner || 'FLOW').replace(/[^A-Za-z0-9]/g, '_')}_${String(config.messageType || 'MESSAGE')}_INBOUND`.toUpperCase();
  return {
    name: `${String(config.partner)} ${String(config.messageType)} inbound`,
    code,
    description: 'Guided EAI Studio flow',
    versionLabel: `v${Date.now()}`,
    inboundChannel: { protocol: config.protocol, endpoint: config.endpoint, mode: config.mode, timeoutMs: Number(config.timeout) * 1000, idempotencyKey: config.idempotencyKey },
    parser: { format: config.messageType },
    validator: { schemaRef: config.schema, requiredFields: ['manifestNo'] },
    transformer: { mappingVersionId: config.mapping },
    router: { target: config.target, routeTo: config.target },
    connectorInstanceId: typeof config.connectorInstanceId === 'string' ? config.connectorInstanceId : undefined,
    destinationPartnerId: typeof config.destinationPartnerId === 'string' ? config.destinationPartnerId : undefined,
    autoDeliver: Boolean(config.autoDeliver),
    mappingVersionId: typeof config.mapping === 'string' ? config.mapping : undefined,
    retryPolicy: { maxAttempts: parseInt(String(config.retry).replace(/\D/g, ''), 10) || 3, backoffMs: 1000, strategy: 'EXPONENTIAL' },
    dlqPolicy: { enabled: Boolean(config.dlq), replay: Boolean(config.replay), quarantine: true },
    steps: undefined,
  };
}

function defaultFlowBuilderConfig(): FlowBuilderConfig {
  return {
    partner: '', protocol: 'REST', endpoint: '', mode: 'Async', messageType: MARITIME_DOCUMENT_TYPE_OPTIONS[0],
    schema: '', mapping: '', target: 'OSS internal app', retry: '3', dlq: true, replay: true,
    timeout: '30', idempotencyKey: 'correlationId', testPayload: cuscarSample, autoDeliver: false,
  };
}

function readFlowBuilderDraft(): { step: number; config: FlowBuilderConfig; createdFlow: FlowRecord | null } | null {
  try {
    const raw = sessionStorage.getItem(FLOW_BUILDER_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { step?: unknown; config?: Partial<FlowBuilderConfig>; createdFlow?: FlowRecord };
    if (!parsed.config || typeof parsed.step !== 'number') return null;
    return { step: Math.max(0, Math.min(5, parsed.step)), config: { ...defaultFlowBuilderConfig(), ...parsed.config }, createdFlow: parsed.createdFlow ?? null };
  } catch { return null; }
}

function transformCuscarToCanonical(source: Record<string, unknown>) {
  const containers = Array.isArray(source.containers) ? source.containers as Array<Record<string, unknown>> : [];
  return {
    cargoManifest: {
      reference: source.manifestNo ?? 'UNKNOWN',
      messageType: 'CUSCAR',
      receivedFrom: readPath(source, ['declarant', 'code']) ?? 'UNKNOWN',
    },
    vessel: {
      imo: readPath(source, ['vessel', 'imo']),
      name: readPath(source, ['vessel', 'name']),
      voyage: readPath(source, ['vessel', 'voyage']),
    },
    ports: {
      load: readPath(source, ['port', 'load']),
      discharge: readPath(source, ['port', 'discharge']),
    },
    containers: containers.map((container) => ({
      number: container.number,
      equipmentType: container.isoCode,
      grossWeightKg: container.weightKg,
      sealNumber: container.seal,
    })),
  };
}

function readPath(value: unknown, path: string[]): unknown {
  return path.reduce<unknown>((current, segment) => (current && typeof current === 'object' ? (current as Record<string, unknown>)[segment] : undefined), value);
}

function toggle<T>(items: T[], item: T): T[] {
  return items.includes(item) ? items.filter((candidate) => candidate !== item) : [...items, item];
}

function validateMappingRules(rules: MappingRuleDraft[]): string {
  const missing = rules.filter((rule) => !rule.targetPath || (!rule.expression && rule.type !== 'direct'));
  if (missing.length) return `${missing.length} rules need a target path and expression before publishing.`;
  return `${rules.length} mapping rules validated. Draft can be tested in the Functional Test Lab.`;
}

function nextVersion(version: string): string {
  const number = Number(version.replace(/^v/i, ''));
  return Number.isFinite(number) ? `v${number + 1}` : `${version}-next`;
}

function protocolDescription(protocol: ConnectorProtocolFamily): string {
  const descriptions: Record<ConnectorProtocolFamily, string> = {
    REST: 'Inbound APIs and outbound REST delivery.',
    WEBHOOK: 'Partner callbacks and event notifications.',
    SFTP: 'Folder-based inbound and outbound batch exchange.',
    KAFKA: 'Topic-based streaming and async delivery.',
    SOAP: 'Enterprise SOAP/XML service integration.',
    MANUAL: 'Manual upload and test-only channels.',
  };
  return descriptions[protocol];
}

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase());
}
